import React, { useState } from 'react';
import classNames from 'classnames';
import { currency } from 'Helpers/Formats';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import StyledTooltip from 'Components/UI/StyledTooltip';
import PropTypes from 'prop-types';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { ListItemIcon, ListItemSecondaryAction } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import { Link } from 'react-router-dom';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { clientManagedAccountUri } from 'Consts/clientUris';
import useMobileLayout from 'Hooks/useMobileLayout';
import { hasRelationship } from 'Helpers/Affordances';
import fr from 'facet-rest';
import { buildClientLink } from 'Views/Page/Content/Templates';

const styles = theme => ({
  root: {},
  divider: {
    borderBottom: '1px solid',
    borderBottomColor: lighten(theme.palette.facet.slate, 0.10),
  },
  rootColorAccentBlue: {
    color: theme.palette.facet.accentBlue,
  },
  rootColorAccentGreen: {
    color: theme.palette.facet.accentGreen,
  },
  secondaryDetail: {
    paddingLeft: theme.spacing(2),
    textAlign: 'right',
    flexShrink: 0,
  },
  syncError: {
    color: theme.palette.facet.red,
  },
});

const OnClickItem = ({
  className,
  classes,
  name,
  category,
  balance,
  onClick,
  syncStatus,
  color,
  resource,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { householdResource } = useSimpleHousehold();
  const isMobile = useMobileLayout();

  // Build the managed account route based on a frontend feature toggle
  let clientManagedAccountRoute;
  let clientManagedAccountPageCtxRoute;
  // Get the managed account relationship url
  const managedAccountLink = resource.relationship(fr.relationships.managedAccount.name)?.link('related').url();
  // If the managedAccountPageContextRefactor toggle is enabled, build the page context route
  if (hasRelationship(resource, fr.relationships.accountOpeningWizard.name) && hasRelationship(resource, fr.relationships.managedAccount.name)) {
    clientManagedAccountPageCtxRoute = buildClientLink(managedAccountLink);
  }
  // If the managedAccount relationship exists, build the legacy client managed account route
  if (hasRelationship(resource, fr.relationships.managedAccount.name)) {
    clientManagedAccountRoute = clientManagedAccountUri(householdResource.id(), managedAccountLink, null, isMobile);
  }
  // If the managedAccountPage relationship exists
  if (hasRelationship(resource, fr.relationships.managedAccountPage.name)) {
    const managedAccountPageLink = resource.relationship(fr.relationships.managedAccountPage.name)?.link('related').url();
    clientManagedAccountPageCtxRoute = buildClientLink(managedAccountPageLink);
  }

  // If the page context route is populated use that, otherwise use the legacy route
  const navLink = clientManagedAccountPageCtxRoute || clientManagedAccountRoute;

  return (
    <ListItem
      test={`balance-sheet-item-${resource.id()}`}
      component={Link}
      button
      to={navLink}
      onClick={onClick}
      className={classNames(className, classes.root, {
        [classes.rootColorAccentBlue]: color === 'accentBlue',
        [classes.rootColorAccentGreen]: color === 'accentGreen',
      })}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)} // <-- onMouseOver must be accompanied by onFocus for accessibility
    >
      {syncStatus && syncStatus !== 'good' && (
        <ListItemIcon>
          <StyledTooltip
            arrow
            interactive
            title="There was an issue syncing. Please see account for details."
            placement="top"
          >
            <ErrorIcon className={classes.syncError} />
          </StyledTooltip>
        </ListItemIcon>)
      }
      <ListItemText
        primary={name}
        primaryTypographyProps={{
          variant: 'h5',
          noWrap: true,
        }}
      />
      <ListItemText
        className={classes.secondaryDetail}
        primary={(() => {
          if (category === 'real') {
            return isHovered ? 'View Real Asset' : currency(parseFloat(balance), false);
          }
          return isHovered ? 'View Account' : currency(parseFloat(balance), false);
        })()}
        primaryTypographyProps={{
          variant: 'h5',
          noWrap: true,
        }}
      />
      <ListItemSecondaryAction />
    </ListItem>
  );
};

OnClickItem.defaultProps = {
  className: '',
  resource: {},
};

OnClickItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  balance: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  syncStatus: PropTypes.string.isRequired,
  color: PropTypes.oneOf(['accentBlue', 'accentGreen']).isRequired,
  resource: PropTypes.object,
};

export default withStyles(styles)(OnClickItem);
